import type { UseQueryResult } from "@tanstack/react-query";
import type { UseFormReturn } from "react-hook-form";

import { useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";

import type { GetMyAccountQuery, GigClaim, Setlist } from "@/api/queries";

import {
  useDeleteGigClaim,
  usePostGigClaim,
  useS3Put,
  useUpdateGigClaim,
} from "@/api/mutations";
import { useGetMyProfile } from "@/api/queries";
import { sendEvent, sendProperty } from "@/helpers/helpers";

import type { GigClaimFormValues } from "../utils/gigsClaimSchema";

import { buildClaimPayload } from "../utils/gigsClaimUtils";

type useGigsClaimMutationProps = {
  form: UseFormReturn<GigClaimFormValues>;
  id: string | undefined;
  gigClaims: UseQueryResult<GigClaim[], Error>;
  refetchGigClaim: () => Promise<unknown>;
  myAccount: GetMyAccountQuery;
  setlists: UseQueryResult<Setlist[], Error>;
};

type useGigClaimMutationReturn = {
  handleEvidenceChange: (file: File, fieldName: "evidenceURL" | "proofOfPerformance") => void;
  pending: boolean;
  mutationErrorMessage: string;
  addClaim: (adminRights: boolean) => Promise<void>;
  updateClaim: (adminRights: boolean) => Promise<void>;
  deleteClaim: (adminRights: boolean) => Promise<void>;
};

export default function useGigsClaimMutation({
  form,
  id,
  gigClaims,
  refetchGigClaim,
  myAccount,
  setlists,
}: useGigsClaimMutationProps): useGigClaimMutationReturn {
  const { t } = useTranslation(["gigs", "common"]);
  const navigate = useNavigate();
  const profile = useGetMyProfile();

  const [pending, setPending] = useState(false);
  const [mutationErrorMessage, setMutationErrorMessage] = useState("");

  // Refs persist across renders — fixes the render-reset bug from bare `let` variables
  const evidenceNameRef = useRef<string | null>(null);
  const evidenceTypeRef = useRef<string | null>(null);
  const putUrlRef = useRef<string | null>(null);
  // Tracks the in-flight S3 upload so submit can await it before saving the claim
  const uploadPromiseRef = useRef<Promise<void> | null>(null);

  const createMutation = usePostGigClaim();
  const updateMutation = useUpdateGigClaim();
  const deleteMutation = useDeleteGigClaim();
  const putS3 = useS3Put();

  async function startReqAndCheckValidation(): Promise<boolean> {
    setPending(true);
    form.clearErrors("root");
    createMutation.reset();
    updateMutation.reset();
    const valid = await form.trigger();
    if (!valid)
      return false;
    // Duplicate date+time check — always runs
    if (gigClaims?.data) {
      const { time, date } = form.getValues();
      const hasDuplicateDateTime = gigClaims.data.some(g =>
        String(g.id) !== String(id)
        && g.time === time
        && g.date.slice(0, 10) === date.slice(0, 10),
      );
      if (hasDuplicateDateTime) {
        form.setError("root", {
          message: t("gigs:claim-errors-existing-date-time", "Invalid date and time, please try again") ?? undefined,
        });
        return false;
      }
    }
    return true;
  }
  function handleSuccess(message: string) {
    toast.success(message);
    void navigate({ to: "/gigs" });
    void gigClaims.refetch();
    void refetchGigClaim?.();
    sendProperty({
      property: "last_gig_added",
      value: "CURRENT_DATE",
      email: profile?.data?.email,
    });
    sendEvent({ property: "pe14489857_last_gig_added" });
    setPending(false);
  }

  function handleError(error: Error) {
    setMutationErrorMessage(error.message);
    toast.error(error.message);
    setPending(false);
  }

  const uploadEvidence = async (file: File): Promise<void> => {
    if (file == null)
      return;
    const uploadToastId = toast.loading(t("gigs:evidence-uploading", "Uploading evidence..."));
    try {
      const res = await putS3.mutateAsync({
        key: evidenceNameRef.current ?? "",
        contenttype: evidenceTypeRef.current ?? "",
      });
      putUrlRef.current = res.putURL ?? null;
      if (putUrlRef.current == null) {
        toast.error(t("gigs:evidence-upload-failed", "Evidence upload failed — no upload URL returned."), { id: uploadToastId });
        return;
      }
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.addEventListener("error", reject);
        xhr.open("PUT", putUrlRef.current!, true);
        xhr.setRequestHeader("Content-Type", evidenceTypeRef.current ?? "");
        xhr.onload = () => {
          if (xhr.status === 200) {
            resolve();
          }
          else {
            reject(new Error(`S3 upload failed with status: ${xhr.status}`));
          }
        };
        xhr.send(file);
      });
      toast.success(t("gigs:evidence-upload-success", "Evidence uploaded successfully."), { id: uploadToastId });
    }
    catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      toast.error(t("gigs:evidence-upload-failed", `Evidence upload failed: ${message}`), { id: uploadToastId });
      form.setError("root", { message: "There was an error attempting to upload evidence." });
    }
  };

  function handleEvidenceChange(file: File, fieldName: "evidenceURL" | "proofOfPerformance"): void {
    if (file == null)
      return;
    const key = uuidv4();
    evidenceNameRef.current = `${key}-${file.name}`;
    evidenceTypeRef.current = file.type;
    form.setValue(fieldName, evidenceNameRef.current);
    uploadPromiseRef.current = uploadEvidence(file);
  }

  const addClaim = async (adminRights: boolean): Promise<void> => {
    if (!adminRights)
      return;
    const valid = await startReqAndCheckValidation();
    if (!valid) {
      setPending(false);
      return;
    }
    try {
      // Wait for any in-flight evidence upload to complete before saving
      if (uploadPromiseRef.current != null)
        await uploadPromiseRef.current;
      const data = buildClaimPayload(form.getValues(), myAccount, setlists);
      await createMutation.mutateAsync(data);
      handleSuccess(t("gigs:updated-gig-toast", "Gig Added"));
    }
    catch (error: unknown) {
      handleError(error instanceof Error ? error : new Error(String(error)));
    }
  };

  const updateClaim = async (adminRights: boolean): Promise<void> => {
    if (!adminRights)
      return;
    const valid = await startReqAndCheckValidation();
    if (!valid) {
      setPending(false);
      return;
    }
    try {
      // Wait for any in-flight evidence upload to complete before saving
      if (uploadPromiseRef.current != null)
        await uploadPromiseRef.current;
      const data = buildClaimPayload(form.getValues(), myAccount, setlists, id!);
      await updateMutation.mutateAsync(data);
      handleSuccess(t("gigs:updated-gig-toast", "Gig Updated"));
    }
    catch (error: unknown) {
      handleError(error instanceof Error ? error : new Error(String(error)));
    }
  };

  const deleteClaim = async (adminRights: boolean): Promise<void> => {
    if (!adminRights)
      return;
    setPending(true);
    try {
      await deleteMutation.mutateAsync({
        id: id!,
        account_code: myAccount.data!.artistaccountcode,
      });
      handleSuccess(t("gigs:gig-deleted-toast", "Gig Deleted"));
    }
    catch (error: unknown) {
      handleError(error instanceof Error ? error : new Error(String(error)));
    }
    finally {
      setPending(false);
    }
  };

  return {
    handleEvidenceChange,
    pending,
    mutationErrorMessage,
    addClaim,
    updateClaim,
    deleteClaim,
  };
}
